import { RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SelectInput } from '@/components/generals/form-fields'

export const EXPERIENCE_OPTIONS = [
    { label: 'Any experience', min: 0 },
    { label: '5+ years', min: 5 },
    { label: '10+ years', min: 10 },
    { label: '20+ years', min: 20 }
]

export const AVAILABILITY_OPTIONS = ['Any time', 'Mornings', 'Afternoons', 'Evenings', 'Weekends']

export interface AdvisorFilterState {
    specialty: string
    experience: string
    availability: string
}

export const DEFAULT_FILTERS: AdvisorFilterState = {
    specialty: 'All specialties',
    experience: EXPERIENCE_OPTIONS[0].label,
    availability: AVAILABILITY_OPTIONS[0]
}

interface AdvisorFiltersProps {
    specialties: Array<string>
    value: AdvisorFilterState
    onChange: (value: AdvisorFilterState) => void
    resultCount: number
    className?: string
}

export const AdvisorFilters = ({ specialties, value, onChange, resultCount, className }: AdvisorFiltersProps) => {
    const changed = value.specialty !== DEFAULT_FILTERS.specialty || value.experience !== DEFAULT_FILTERS.experience || value.availability !== DEFAULT_FILTERS.availability

    return (
        <div role='search' aria-label='Narrow the advisor list' className={cn('glass rounded-[28px] p-3 sm:rounded-full sm:p-2', className)}>
            <div className='grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-center'>
                <label className='sr-only' htmlFor='filter-specialty'>
                    Filter by specialty
                </label>

                <SelectInput
                    id='filter-specialty'
                    value={value.specialty}
                    onChange={event => onChange({ ...value, specialty: event.target.value })}
                    className='rounded-full bg-black/30'
                >
                    {['All specialties', ...specialties].map(option => <option key={option} value={option}>{option}</option>)}
                </SelectInput>

                <label className='sr-only' htmlFor='filter-experience'>
                    Filter by experience
                </label>

                <SelectInput
                    id='filter-experience'
                    value={value.experience}
                    onChange={event => onChange({ ...value, experience: event.target.value })}
                    className='rounded-full bg-black/30'
                >
                    {EXPERIENCE_OPTIONS.map(option => <option key={option.label} value={option.label}>{option.label}</option>)}
                </SelectInput>

                <label className='sr-only' htmlFor='filter-availability'>
                    Filter by availability
                </label>

                <SelectInput
                    id='filter-availability'
                    value={value.availability}
                    onChange={event => onChange({ ...value, availability: event.target.value })}
                    className='rounded-full bg-black/30'
                >
                    {AVAILABILITY_OPTIONS.map(option => <option key={option} value={option}>{option === 'Any time' ? 'Any day or time' : option}</option>)}
                </SelectInput>

                <div className='flex items-center justify-between gap-3 px-2 sm:justify-end sm:pl-3 sm:pr-2'>
                    <p className='whitespace-nowrap text-xs text-cream/55' aria-live='polite'>
                        {resultCount} {resultCount === 1 ? 'advisor shown' : 'advisors shown'}
                    </p>

                    <button
                        type='button'
                        onClick={() => onChange(DEFAULT_FILTERS)}
                        disabled={!changed}
                        className='inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-xs text-cream/70 transition-colors hover:bg-white/10 hover:text-cream disabled:pointer-events-none disabled:opacity-30'
                    >
                        <RotateCcw aria-hidden='true' className='size-3.5' />
                        Clear
                    </button>
                </div>
            </div>
        </div>
    )
}
